/**
 * A waitlist lead's offer status (#1960): whether the household it led to is on the waitlist's 50% first year, as
 * functions/lead_offer_status_service.js writes it to `waitlist_leads/{id}.offer_status`. The webhook, a daily sweep
 * and "Check with Apple" keep it fresh; this only reads it.
 */
import { BADGE } from "../ui/classes.js";
import { solidIcon } from "../ui/icons.js";

/** Each state's words and badge. Class strings are whole, for Tailwind. */
export const OFFER_STATES = {
  trial_50_at_renewal: { label: "Trial, 50% at renewal", badge: BADGE.green, icon: "check-circle", help: "In the trial; converts at the waitlist's 50%." },
  paying_50: { label: "On the 50%", badge: BADGE.green, icon: "check-circle", help: "Paying the waitlist's 50%." },
  trial_regular_at_renewal: { label: "Trial, full price at renewal", badge: BADGE.red, icon: "exclamation-triangle", help: "In the trial without the 50%: converts at the regular price." },
  paying_regular: { label: "Paying full price", badge: BADGE.yellow, icon: "exclamation-triangle", help: "Paying the regular price." },
  not_claimed: { label: "Not claimed", badge: BADGE.yellow, icon: "ticket", help: "Got a code or the offer, but no household is linked to the lead." },
  no_subscription: { label: "No subscription", badge: BADGE.gray, icon: "x-circle", help: "The household has nothing running." },
  wont_renew: { label: "Won't renew", badge: BADGE.gray, icon: "x-circle", help: "Auto-renew is off." },
  household: { label: "Paid by household", badge: BADGE.blue, icon: "credit-card", help: "Another member of the household pays for Savvy." },
  other_store: { label: "Other store", badge: BADGE.blue, icon: "credit-card", help: "Neither App Store nor Google Play (a promotional grant, say)." },
  unknown: { label: "Unknown", badge: BADGE.gray, icon: "exclamation-triangle", help: "Apple could not be asked; the error says why." }
};

const NOT_CHECKED = { label: "Not checked", badge: BADGE.gray, icon: "clock", help: "No status yet: the daily sweep or Check with Apple fills it in." };

/** The Leads list's Offer filter: one state, or a group of them. */
export const OFFER_FILTERS = {
  ALL: () => true,
  NEEDS_NUDGE: (state) => state === "trial_regular_at_renewal" || state === "not_claimed",
  ON_OFFER: (state) => state === "trial_50_at_renewal" || state === "paying_50",
  NOT_ON_OFFER: (state) => state === "trial_regular_at_renewal" || state === "paying_regular",
  NOT_CHECKED: (state) => !state,
  ...Object.fromEntries(Object.keys(OFFER_STATES).map((key) => [key, (state) => state === key]))
};

/** Whether the lead was handed the 50%: an iOS code, recipes claimed, or the Android offer shown (wasHandedOut). */
export function wasHandedOffer(lead) {
  return Boolean(lead?.promo_code || lead?.claimed_by_family_id || lead?.claimed_at
    || (Array.isArray(lead?.android_offer_uids) && lead.android_offer_uids.length));
}

export function offerState(lead) {
  return lead?.offer_status?.state || null;
}

/** Whether a lead passes the Offer filter's value. Only leads handed the 50% have a status or count as not checked. */
export function matchesOfferFilter(lead, filter) {
  if (!filter || filter === "ALL") return true;
  const test = OFFER_FILTERS[filter];
  if (!test) return true;
  if (filter === "NOT_CHECKED") return wasHandedOffer(lead) && !offerState(lead);
  return test(offerState(lead));
}

/** Counts for the stats row. */
export function countOfferStatuses(leads) {
  const counts = { on_offer: 0, full_price_trials: 0, not_claimed: 0, not_checked: 0 };
  for (const lead of leads) {
    const state = offerState(lead);
    if (OFFER_FILTERS.ON_OFFER(state)) counts.on_offer++;
    else if (state === "trial_regular_at_renewal") counts.full_price_trials++;
    else if (state === "not_claimed") counts.not_claimed++;
    else if (!state && wasHandedOffer(lead)) counts.not_checked++;
  }
  return counts;
}

function shortDate(iso) {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, { day: "numeric", month: "short", year: date.getFullYear() === new Date().getFullYear() ? undefined : "numeric" });
}

function escapeText(value) {
  return String(value ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#039;");
}

/** The badge alone, with the state's meaning as its title. */
export function offerStatusBadgeHTML(status) {
  const meta = (status?.state && OFFER_STATES[status.state]) || NOT_CHECKED;
  const title = status?.error ? `${meta.help} Last check failed: ${status.error}` : meta.help;
  return `<span data-offer-state="${escapeText(status?.state || "")}" title="${escapeText(title)}" class="${meta.badge}">${solidIcon(meta.icon, "size-3.5")}${escapeText(meta.label)}</span>`;
}

/** The badge with when it renews and when it was checked, for a table cell. */
export function offerStatusCellHTML(status) {
  if (!status?.state) return offerStatusBadgeHTML(status);
  const renews = shortDate(status.renews_at);
  const checked = shortDate(status.checked_at);
  const lines = [
    renews ? `${status.state === "wont_renew" ? "Ends" : "Renews"} ${escapeText(renews)}${status.renewal_price ? ` · ${escapeText(status.renewal_price)}` : ""}` : "",
    checked ? `Checked ${escapeText(checked)}${status.apple_checked ? " with Apple" : ""}` : ""
  ].filter(Boolean);
  const error = status.error ? `<span class="text-xs text-red-600 dark:text-red-400">${solidIcon("exclamation-triangle", "inline size-3.5")} ${escapeText(status.error)}</span>` : "";
  return `
    <div class="flex flex-col items-start gap-1">
      ${offerStatusBadgeHTML(status)}
      ${lines.map((line) => `<span class="text-xs whitespace-nowrap text-gray-500 dark:text-gray-400">${line}</span>`).join("")}
      ${error}
    </div>`;
}

/** Each field of a status as [label, text] rows, for the lead drawer and the user card. */
export function offerStatusRows(status) {
  if (!status?.state) return [];
  const platform = status.platform === "ios" ? "iOS" : status.platform === "android" ? "Android" : null;
  const link = { claim: "recipes claimed into it", android_offer: "the account the Android offer was shown to", email: "an account with the lead's email" }[status.link] || null;
  return [
    ["Store", [platform, status.store].filter(Boolean).join(" · ") || null],
    ["Product", status.product_id],
    ["Period", status.period === "trial" ? "Free trial" : status.period === "paid" ? "Paid" : null],
    [status.state === "wont_renew" ? "Ends" : "Renews", status.renews_at ? new Date(status.renews_at).toLocaleString() : null],
    ["Renewal price", status.renewal_price],
    ["Offer", status.offer_name],
    ["Household", status.family_id],
    ["Linked by", link],
    ["Checked", status.checked_at ? `${new Date(status.checked_at).toLocaleString()} (${status.source || "?"}${status.apple_checked ? ", asked Apple" : ""})` : null],
    ["Last error", status.error ? `${status.error}${status.error_at ? ` (${new Date(status.error_at).toLocaleString()})` : ""}` : null]
  ].filter(([, value]) => value);
}
